import React from "react";
import { Link } from "react-router-dom";
import { getBrand } from "../../utils/branding";
import { getLegalContent } from "../../utils/legalContent";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, Scale } from "lucide-react";

export default function AUP() {
  const brand = getBrand();
  const c = getLegalContent();

  return (
    <div className="au-root">
      <header style={hdr}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Back to Home
        </Link>
      </header>

      <div className="au-container" style={{ maxWidth: 820 }}>
        <div className="au-glass au-pad-lg" style={{ marginBottom: 56 }}>
          <div className="au-eyebrow" style={{ marginBottom: 14 }}>
            ACCEPTABLE USE · {c.lastUpdated.toUpperCase()}
          </div>
          <h1 style={h1}>Acceptable <span style={{ color: "var(--au-accent-ink)" }}>Use Policy</span></h1>
          <p className="au-lead" style={{ marginTop: 16, marginBottom: 32 }}>
            {c.fullName} is a {c.productNoun}. With great power comes great responsibility. Here's what's allowed.
          </p>

          <div style={callout}>
            <div style={calloutTitle}>
              <Scale size={16} style={icoInline} /> Legal Compliance Required
            </div>
            <p style={calloutBody}>
              Unauthorized {c.actionVerb}ning of third-party systems is illegal under the Indian IT Act 2000 (Sections 43, 66) and similar laws worldwide. Violation = immediate account termination + potential legal action.
            </p>
          </div>

          <S n="1" t="ALLOWED Use" color="var(--au-success)">
            <ul style={ul}>
              <li>{c.actionVerb} systems you own (your website, your servers)</li>
              <li>{c.actionVerb} systems you have written permission to {c.actionVerb}</li>
              <li>{c.actionVerb} targets in public bug bounty programs (HackerOne, Bugcrowd)</li>
              <li>Authorized penetration testing for clients</li>
              <li>Educational use on systems you control (CTFs, labs)</li>
            </ul>
          </S>

          <S n="2" t="PROHIBITED Use" color="var(--au-error)">
            <ul style={ul}>
              <li>Unauthorized {c.actionVerb}ning of third-party systems</li>
              <li>Bypassing security controls without permission</li>
              <li>Government, military, or critical infrastructure targets</li>
              <li>Banking/financial systems without explicit authorization</li>
              <li>Healthcare systems with patient data</li>
              <li>Any system protected by laws you'd violate</li>
              <li>Stress testing as denial-of-service</li>
            </ul>
          </S>

          <S n="3" t="Account Misuse">
            <ul style={ul}>
              <li>No sharing accounts</li>
              <li>No reselling our service</li>
              <li>No automated abuse (bot networks, scraping)</li>
              <li>No circumventing rate limits</li>
              <li>No fake KYC information</li>
            </ul>
          </S>

          <S n="4" t="Reporting Violations">
            <p>If you see misuse of {c.fullName}, report it:</p>
            <ul style={ul}>
              <li>Email: <a href={`mailto:abuse@${c.parentDomain}`} style={link}>abuse@{c.parentDomain}</a></li>
              <li>Include: account email, target {c.actionVerb}ned, evidence</li>
              <li>We respond within 24 hours</li>
              <li>Reports kept confidential</li>
            </ul>
          </S>

          <S n="5" t="Enforcement">
            <p>Violations result in:</p>
            <ul style={ul}>
              <li><strong>First offense:</strong> Warning + temporary suspension</li>
              <li><strong>Second offense:</strong> Permanent account termination</li>
              <li><strong>Severe violation:</strong> Immediate termination + report to authorities</li>
              <li><strong>Illegal activity:</strong> Cooperation with law enforcement</li>
            </ul>
          </S>

          <S n="6" t="Your Responsibility">
            <p>By using {c.fullName}, you confirm:</p>
            <ul style={ul}>
              <li>You're 18+ years old</li>
              <li>You own or have permission to {c.actionVerb} every target</li>
              <li>You'll comply with all applicable laws</li>
              <li>You'll bear any consequences of unauthorized use</li>
            </ul>
          </S>

          <S n="7" t="Contact">
            <p>Questions about acceptable use? Email <a href={`mailto:${c.legalEmail}`} style={link}>{c.legalEmail}</a></p>
          </S>

        </div>
      </div>
      <Footer />
    </div>
  );
}

function S({ n, t, children, color }) {
  return (
    <div style={section}>
      <h2 style={h2}>
        <span style={{ color: color || "var(--au-accent-ink)", marginRight: 10 }}>{n}.</span>{t}
      </h2>
      <div style={body}>{children}</div>
    </div>
  );
}

const hdr = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, maxWidth: 1200, margin: "0 auto", padding: "28px 24px 8px" };
const h1 = { fontSize: 42, margin: 0 };
const section = { marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--au-hair)" };
const h2 = { fontSize: 20, marginBottom: 16, fontWeight: 700 };
const body = { color: "var(--au-text-2)", fontSize: 14, lineHeight: 1.8 };
const ul = { margin: "8px 0", paddingLeft: 22 };
const link = { color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 };
const callout = { padding: "20px 24px", background: "var(--au-glass)", border: "1px solid var(--au-border)", borderRadius: 14, marginBottom: 32 };
const calloutTitle = { fontWeight: 700, color: "var(--au-accent-ink)", fontSize: 14, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 };
const calloutBody = { margin: 0, fontSize: 13, color: "var(--au-text-2)", lineHeight: 1.7 };
const icoInline = { flex: "none" };
const copyright = { marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--au-hair)", fontSize: 12, color: "var(--au-muted)", textAlign: "center" };
